import React, { useState, useEffect } from "react";
import { apiFetch } from "../services/api";
import { clearEnrichmentClientJobCache } from "../utils/enrichmentClientCache";
import { clearAgentChatClientState } from "../utils/agentChatClientState";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

function dbCleanupCopy(embedded) {
  if (!embedded) {
    return {
      title: "Database Cleanup",
      loadingStats: "Loading database statistics...",
      statsTitle: "Current Database Statistics",
      items: "Entities",
      connections: "Relationships",
      cleanupActions: "Cleanup Actions",
      clearByType: "Clear By Entity Type",
      clearByTypeDesc:
        "Delete selected entity types and their associated relationships",
      clearAll: "Clear All Data",
      clearAllDesc: "Remove all entities and relationships",
      noTypes: "No entity types available",
      loadTypesError: "Failed to load entity types",
      selectOneType: "Please select at least one entity type",
      clearingAll: "Clearing all database data...",
      clearingSelected: "Clearing selected entity types...",
      errorStats: "Error fetching database statistics",
      errorClear: "Error clearing database",
      errorClearTypes: "Error clearing selected entity types",
      confirmSelected: (count) =>
        `Are you sure you want to clear selected entity types (${count})? This will remove matching entities and all their relationships.`,
      confirmAll:
        "Are you sure you want to clear ALL data? This will permanently remove all entities, relationships, and agent chat history. This action cannot be undone!",
    };
  }
  return {
    title: "Clear map data",
    loadingStats: "Loading map statistics...",
    statsTitle: "On your map",
    items: "Items",
    connections: "Connections",
    cleanupActions: "What to clear",
    clearByType: "Clear by item type",
    clearByTypeDesc: "Delete selected item types and their connections",
    clearAll: "Clear everything",
    clearAllDesc: "Remove all items, connections, and agent chat history from your map",
    noTypes: "No item types available",
    loadTypesError: "Failed to load item types",
    selectOneType: "Please select at least one item type",
    clearingAll: "Clearing all map data...",
    clearingSelected: "Clearing selected item types...",
    errorStats: "Error loading map statistics",
    errorClear: "Error clearing map data",
    errorClearTypes: "Error clearing selected item types",
    confirmSelected: (count) =>
      `Are you sure you want to clear ${count} selected item type(s)? This removes matching items and all their connections.`,
    confirmAll:
      "Are you sure you want to clear everything on your map? This permanently removes all items, connections, and agent chat history. This cannot be undone!",
  };
}

const DatabaseCleanup = ({ onClose, variant = "legacy" }) => {
  const embedded = variant === "embedded";
  const copy = dbCleanupCopy(embedded);
  const [stats, setStats] = useState(null);
  const [availableEntityTypes, setAvailableEntityTypes] = useState([]);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [message, setMessage] = useState("");
  const [entityTypesLoadError, setEntityTypesLoadError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Fetch available entity types for selector (fallback independent of stats).
  const fetchAvailableEntityTypes = async () => {
    try {
      const response = await apiFetch("/api/entity-types");
      const data = await response.json();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.types)
        ? data.types
        : Array.isArray(data?.entity_types)
        ? data.entity_types
        : [];
      const values = list
        .map((t) => (typeof t?.value === "string" ? t.value.trim() : ""))
        .filter(Boolean);
      const uniqueValues = [...new Set(values)];
      setAvailableEntityTypes(uniqueValues);
      setEntityTypesLoadError(
        uniqueValues.length === 0 ? "No entity types returned by API" : ""
      );
      setSelectedEntityTypes((prev) =>
        prev.length > 0
          ? prev.filter((t) => uniqueValues.includes(t))
          : uniqueValues
      );
    } catch (error) {
      console.error("Error fetching entity types:", error);
      setEntityTypesLoadError(copy.loadTypesError);
    }
  };

  // Fetch database statistics
  const fetchStats = async () => {
    try {
      const response = await apiFetch("/api/database/stats");
      const data = await response.json();
      setStats(data);
      const types = Object.keys(data?.entity_types || {});
      if (types.length > 0) {
        setAvailableEntityTypes((prev) =>
          prev.length > 0 ? [...new Set([...prev, ...types])] : types
        );
        setSelectedEntityTypes((prev) =>
          prev.length > 0 ? prev.filter((t) => types.includes(t)) : types
        );
      }
    } catch (error) {
      console.error("Error fetching database stats:", error);
      setMessage(copy.errorStats);
    }
  };

  useEffect(() => {
    fetchAvailableEntityTypes();
    fetchStats();
  }, []);

  const handleClearAll = async () => {
    setLoading(true);
    setLoadingMessage(copy.clearingAll);
    setMessage("");
    try {
      const response = await apiFetch("/api/database/clear-all", {
        method: "POST",
      });
      const data = await response.json();
      
      // Immediately set stats to 0 to show correct counts right away
      setStats({
        entities: 0,
        relationships: 0,
        jobs: 0,
        file_size_mb: stats?.file_size_mb || 0,
        entity_types: {},
        tables: stats?.tables || {},
      });
      
      setMessage(data.message);
      setLoadingMessage("Refreshing statistics...");
      
      // Refresh stats from database to ensure accuracy
      await fetchStats();

      // Wait a moment for backend cache invalidation to complete
      setLoadingMessage("Updating caches...");
      setTimeout(() => {
        notifyDatabaseChange({ clearAgentChat: true });
        setLoadingMessage("");
      }, 1000);
    } catch (error) {
      console.error("Error clearing database:", error);
      setMessage(copy.errorClear);
      setLoadingMessage("");
    } finally {
      setLoading(false);
    }
  };

  const handleClearSelectedTypes = async () => {
    if (!selectedEntityTypes.length) {
      setMessage(copy.selectOneType);
      return;
    }
    setLoading(true);
    setLoadingMessage("Clearing selected entity types...");
    setMessage("");
    try {
      const response = await apiFetch("/api/database/clear-by-entity-types", {
        method: "POST",
        body: JSON.stringify({ entity_types: selectedEntityTypes }),
      });
      const data = await response.json();
      setMessage(
        `${data.message}. Entities: ${data.entities_cleared}, Relationships: ${data.relationships_cleared}`
      );
      setLoadingMessage("Refreshing statistics...");
      await fetchStats();
      setLoadingMessage("Updating caches...");
      setTimeout(() => {
        notifyDatabaseChange();
        setLoadingMessage("");
      }, 1000);
    } catch (error) {
      console.error("Error clearing selected entity types:", error);
      setMessage(copy.errorClearTypes);
      setLoadingMessage("");
    } finally {
      setLoading(false);
    }
  };

  const toggleEntityType = (entityType) => {
    setSelectedEntityTypes((prev) =>
      prev.includes(entityType)
        ? prev.filter((t) => t !== entityType)
        : [...prev, entityType]
    );
  };

  const selectAllEntityTypes = () => {
    setSelectedEntityTypes(availableEntityTypes);
  };

  const clearEntityTypeSelection = () => {
    setSelectedEntityTypes([]);
  };


  const notifyDatabaseChange = (opts = {}) => {
    clearEnrichmentClientJobCache();
    try {
      localStorage.setItem("dbChangedAt", String(Date.now()));
    } catch {}
    try {
      window.dispatchEvent(
        new CustomEvent("database-changed", {
          detail: { clearAgentChat: Boolean(opts.clearAgentChat) },
        })
      );
    } catch {}

    if (opts.clearAgentChat) {
      clearAgentChatClientState();
    }

    // Clear frontend caches
    try {
      // Clear dashboard cache
      window.dashboardCache = null;
      window.dashboardCacheTime = null;

      // Clear entity types cache
      window.cachedEntityTypes = null;

      // Clear any other cached data
      if (window.cachedEntities) {
        window.cachedEntities = [];
      }

      console.log("🗑️ Frontend caches cleared after database cleanup");
    } catch (e) {
      console.log("Could not clear frontend caches:", e);
    }
  };

  const handleConfirmAction = (action) => {
    setConfirmAction(action);
    setShowConfirm(true);
  };

  const executeAction = () => {
    setShowConfirm(false);
    if (confirmAction === "clearAll") {
      handleClearAll();
    } else if (confirmAction === "clearSelectedTypes") {
      handleClearSelectedTypes();
    }
  };

  const backdropClass = embedded
    ? "fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50"
    : "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50";

  const loadingShellClass = embedded
    ? "bg-white rounded-xl border border-slate-200 shadow-lg p-6 w-full max-w-md mx-4"
    : "bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4";

  if (!stats) {
    return (
      <div className={backdropClass}>
        <div className={loadingShellClass}>
          <div className="text-center">
            <div
              className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4 ${
                embedded ? "border-blue-600" : "border-white"
              }`}
            />
            <p className={embedded ? "text-slate-700" : "text-white"}>
              Loading database statistics...
            </p>
          </div>
        </div>
      </div>
    );
  }

  const panelClass = embedded
    ? "bg-white rounded-xl border border-slate-200 shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
    : "bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto";

  const statsSectionClass = embedded
    ? "bg-slate-50 rounded-lg border border-slate-200 p-4 mb-6"
    : "bg-gray-700 rounded-lg p-4 mb-6";

  const busyBackdropClass = embedded
    ? "fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[70]"
    : "fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70]";

  const busyCardClass = embedded
    ? "bg-white rounded-xl p-8 w-full max-w-md mx-4 border border-slate-200 shadow-xl"
    : "bg-gray-800 rounded-lg p-8 w-full max-w-md mx-4 border border-gray-700 shadow-2xl";

  const confirmBackdropClass = embedded
    ? "fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[60]"
    : "fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]";

  const confirmCardClass = embedded
    ? "bg-white rounded-xl border border-slate-200 shadow-xl p-6 w-full max-w-md mx-4"
    : "bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4";

  return (
    <div className={backdropClass}>
      <div className={panelClass}>
        <div className="flex items-center justify-between mb-6">
          <h2
            className={`text-2xl font-bold ${embedded ? "text-slate-900" : "text-white"}`}
          >
            {copy.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={
              embedded
                ? "text-slate-500 hover:text-slate-800 text-2xl leading-none"
                : "text-gray-400 hover:text-white text-2xl"
            }
          >
            ×
          </button>
        </div>

        {/* Database Statistics */}
        <div className={statsSectionClass}>
          <h3
            className={`text-lg font-semibold mb-4 ${
              embedded ? "text-slate-900" : "text-white"
            }`}
          >
            {copy.statsTitle}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div
                className={`text-2xl font-bold ${
                  embedded ? "text-blue-600" : "text-blue-400"
                }`}
              >
                {stats.entities}
              </div>
              <div
                className={`text-sm ${embedded ? "text-slate-600" : "text-gray-300"}`}
              >
                {copy.items}
              </div>
            </div>
            <div className="text-center">
              <div
                className={`text-2xl font-bold ${
                  embedded ? "text-emerald-600" : "text-green-400"
                }`}
              >
                {stats.relationships}
              </div>
              <div
                className={`text-sm ${embedded ? "text-slate-600" : "text-gray-300"}`}
              >
                Relationships
              </div>
            </div>
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div
            className={`mb-4 p-3 rounded-lg border ${
              message.includes("Error")
                ? embedded
                  ? "border-rose-200 bg-rose-50 text-rose-900"
                  : "bg-red-600 text-white border-transparent"
                : embedded
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "bg-green-600 text-white border-transparent"
            }`}
          >
            {message}
          </div>
        )}

        {/* Cleanup Actions */}
        <div className="space-y-4">
          <h3
            className={`text-lg font-semibold ${embedded ? "text-slate-900" : "text-white"}`}
          >
            {copy.cleanupActions}
          </h3>

          {/* Clear Selected Entity Types */}
          <div
            className={
              embedded
                ? "p-4 bg-amber-50 border border-amber-200 rounded-lg"
                : "p-4 bg-orange-900/20 border border-orange-700/50 rounded-lg"
            }
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4
                  className={`font-medium ${embedded ? "text-slate-900" : "text-white"}`}
                >
                  {copy.clearByType}
                </h4>
                <p
                  className={`text-sm ${embedded ? "text-slate-600" : "text-gray-300"}`}
                >
                  {copy.clearByTypeDesc}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleConfirmAction("clearSelectedTypes")}
                disabled={loading || selectedEntityTypes.length === 0}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm shrink-0"
              >
                Clear Selected
              </button>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={selectAllEntityTypes}
                disabled={loading}
                className={
                  embedded
                    ? "px-3 py-1 text-xs border border-slate-300 bg-white text-slate-700 rounded-md hover:bg-slate-50 disabled:opacity-50"
                    : "px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-500 disabled:opacity-50"
                }
              >
                Select all
              </button>
              <button
                type="button"
                onClick={clearEntityTypeSelection}
                disabled={loading}
                className={
                  embedded
                    ? "px-3 py-1 text-xs border border-slate-300 bg-white text-slate-700 rounded-md hover:bg-slate-50 disabled:opacity-50"
                    : "px-3 py-1 text-xs bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50"
                }
              >
                Clear selection
              </button>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
              {availableEntityTypes.length > 0 ? (
                availableEntityTypes.map((type) => (
                  <label
                    key={type}
                    className={`flex items-center gap-2 text-sm ${
                      embedded ? "text-slate-700" : "text-gray-200"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEntityTypes.includes(type)}
                      onChange={() => toggleEntityType(type)}
                      disabled={loading}
                      className={embedded ? "rounded border-slate-300 text-blue-600" : ""}
                    />
                    <span>
                      {type}
                      {typeof stats?.entity_types?.[type] === "number"
                        ? ` (${stats.entity_types[type]})`
                        : ""}
                    </span>
                  </label>
                ))
              ) : (
                <div
                  className={`text-sm ${embedded ? "text-amber-800" : "text-yellow-300"}`}
                >
                  {entityTypesLoadError || copy.noTypes}
                </div>
              )}
            </div>
          </div>

          {/* Clear All Data */}
          <div
            className={
              embedded
                ? "flex items-center justify-between p-4 bg-rose-50 border border-rose-200 rounded-lg gap-4"
                : "flex items-center justify-between p-4 bg-red-900/20 border border-red-700/50 rounded-lg"
            }
          >
            <div>
              <h4
                className={`font-medium ${embedded ? "text-slate-900" : "text-white"}`}
              >
                Clear All Data
              </h4>
              <p
                className={`text-sm ${embedded ? "text-slate-600" : "text-gray-300"}`}
              >
                {copy.clearAllDesc}
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleConfirmAction("clearAll")}
              disabled={loading}
              className={
                embedded
                  ? "px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm shrink-0"
                  : "px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              }
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className={busyBackdropClass}>
            <div className={busyCardClass}>
              <div className="text-center">
                <div
                  className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4 ${
                    embedded ? "border-blue-600" : "border-blue-500"
                  }`}
                />
                <p
                  className={`text-lg font-semibold mb-2 ${
                    embedded ? "text-slate-900" : "text-white"
                  }`}
                >
                  {loadingMessage || "Processing..."}
                </p>
                <p
                  className={`text-sm ${embedded ? "text-slate-600" : "text-gray-400"}`}
                >
                  Please wait while the operation completes...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {showConfirm && (
          <div className={confirmBackdropClass}>
            <div className={confirmCardClass}>
              <h3
                className={`text-lg font-semibold mb-4 ${
                  embedded ? "text-slate-900" : "text-white"
                }`}
              >
                Confirm Action
              </h3>
              <p
                className={`mb-6 ${embedded ? "text-slate-600" : "text-gray-300"}`}
              >
                {confirmAction === "clearSelectedTypes"
                  ? copy.confirmSelected(selectedEntityTypes.length)
                  : copy.confirmAll}
              </p>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowConfirm(false)}
                  className={
                    embedded
                      ? "flex-1 px-4 py-2 border border-slate-300 bg-white text-slate-800 rounded-lg hover:bg-slate-50 text-sm"
                      : "flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                  }
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={executeAction}
                  className={
                    embedded
                      ? "flex-1 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 text-sm"
                      : "flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  }
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatabaseCleanup;
